import Vue from 'vue';
import api from '../api';

export default {
    namespaced: true,
    state: {
        basket: {
            taxAmount: 0.2,
            package: null,
            products: []
        }
    },
    mutations: {
        setPackage(state, value) {
            state.basket.package = value;
        },
        removePackage(state) {
            state.basket.package = null;
        },
        addProduct(state, value) {
            state.basket.products.push(value);
        },
        removeProduct(state, value) {
            state.basket.products.splice(value, 1);
        },
        removeProducts(state) {
            state.basket.products.splice(0, state.basket.products.length);
        },
        increaseQuantity(state, index) {
            state.basket.products[index].quantity++;
        },
        decreaseQuantity(state, index) {
            state.basket.products[index].quantity--;
        }
    },
    getters: {
        products(state, getters, rootState) {
            return rootState.global.products.map(product => ({
                ...product,
                inSelectedPackage: getters.productInSelectedPackage(product),
                inPurchasedPackage: getters.productInPurchasedPackage(product),
                alreadyPurchased: getters.productAlreadyPurchased(product),
                incompatible: getters.productIncompatible(product)
            }));
        },
        productsHaveLoaded(state, getters) {
            try {
                return Object.prototype.hasOwnProperty.call(getters.products[0], 'inSelectedPackage');
            } catch (e) {
                return false;
            }
        },
        productInSelectedPackage: (state, getters, rootState) => (product) => {
            try {
                return state.basket.package.products.indexOf(product.id) !== -1;
            }
            catch (e) {
                return false;
            }
        },
        productInPurchasedPackage: (state, getters, rootState) => (product) => {
            try {
                return rootState.properties.property.package.products.indexOf(product.id) >= 0;
            }
            catch (e) {
                return false;
            }
        },
        productAlreadyPurchased: (state, getters, rootState) => (product) => {
            try {
                return rootState.properties.property.products.indexOf(product.id) >= 0 || false;
            }
            catch (e) {
                return false;
            }
        },
        productIncompatible: (state, getters, rootState) => (product) => {
            try {
                return state.basket.products.find(basketProduct => [...basketProduct.incompatibilities, ...basketProduct.incompatibles].find(
                    incompatibleProduct => incompatibleProduct === product.id
                )) || false;
            }
            catch (e) {
                return false;
            }
        },
        selectedPackage: state => state.basket.package,
        basket(state) {
            return [
                state.basket.package,
                ...state.basket.products
            ].filter(item => item && item.id);
        },
        subtotal(state) {
            const packageSubtotal = parseFloat((state.basket.package && state.basket.package.price)) || 0;
            const productSubtotal = (state.basket.products.length && parseFloat((state.basket.products.reduce((total, pack) => total + (pack.price * pack.quantity), 0)))) || 0;

            return {
                package: packageSubtotal,
                products: productSubtotal,
                net: packageSubtotal + productSubtotal
            };
        },
        tax(state, getters) {
            const packageTax = (getters.subtotal.package && state.basket.package.taxable && parseFloat(getters.subtotal.package * state.basket.taxAmount)) || 0;
            const productsTax = state.basket.products.length && parseFloat((state.basket.products.filter(product => product.taxable)
                .reduce((total, product) => total + (product.price * product.quantity), 0) * state.basket.taxAmount));

            return {
                package: packageTax,
                products: productsTax,
                net: packageTax + productsTax
            };
        },
        total(state, getters) {
            const packageTotal = getters.subtotal.package + getters.tax.package;
            const productsTotal = getters.subtotal.products + getters.tax.products;
            return {
                package: packageTotal,
                products: productsTotal,
                net: packageTotal + productsTotal
            };
        }
    },
    actions: {
        submitOrder({ commit, state, getters, rootState }, data) {
            const order = {
                products: state.basket.products.map(product => ({
                    id: product.id,
                    quantity: product.quantity
                })),
                subtotal: getters.subtotal.net,
                tax: getters.tax.net,
                total: getters.total.net,
                ...data
            };

            if (rootState.properties.property) {
                order.property_id = rootState.properties.property.id;
                order.package_id = rootState.properties.property.package.id;
            }
            else {
                order.package_id = state.basket.package.id;
            }

            if (!order.type) {
                order.type = rootState.properties.property.transaction_type;
            }

            return api.post('orders', order);
        },
        setPackage({ commit, state, rootState }, data) {
            const selected = state.basket.package && state.basket.package.id;
            if (selected !== data.id) {
                state.basket.products.splice(0, state.basket.products.length);
                commit('setPackage', rootState.global.packages.find(pack => pack.id === data.id));
            }
        },
        addProduct({ commit, state, getters }, product) {
            if (product.dependencies.length) {
                const dependencies = getters.products.filter(rootProduct => product.dependencies.indexOf(rootProduct.id) !== -1
                        && state.basket.products.findIndex(basketProduct => basketProduct.id === rootProduct.id) === -1
                        && !rootProduct.inSelectedPackage
                        && !rootProduct.inPurchasedPackage
                        && !rootProduct.alreadyPurchased)
                    .map(rootProduct => rootProduct.name);

                if (dependencies.length) {
                    let dependencyList = dependencies.join(', ');

                    if (dependencies.length > 1) {
                        const lastItem = dependencies.pop();
                        dependencyList = `${dependencies.join(', ')} and ${lastItem}`;
                    }

                    Vue.toasted.info(`${product.name} requires ${dependencyList}`);
                    return false;
                }
            }

            const existing = state.basket.products.findIndex(basketProduct => basketProduct && basketProduct.id === product.id);

            if (existing !== -1 && state.basket.products[existing] && state.basket.products[existing].id && !product.singular) {
                commit('increaseQuantity', existing);
            }
            else if (existing === -1) {
                commit('addProduct', {
                    ...product,
                    quantity: 1
                });
            }
            else {
                Vue.toasted.error('You can only add one of this extra to the basket');
                return false;
            }

            return true;
        },
        removePackage({ commit }) {
            commit('removePackage');
            commit('removeProducts');
        },
        removeProduct({ commit, state }, product) {
            const existing = state.basket.products.findIndex(basketProduct => basketProduct.id === product.id);
            if (existing !== -1 && !product.singular && product.quantity > 1) {
                commit('decreaseQuantity', existing);
            }
            else {
                const dependents = state.basket.products.filter(basketProduct => basketProduct.dependencies.indexOf(product.id) !== -1);
                commit('removeProduct', existing);
                if (dependents.length) {
                    dependents.forEach(dependent => commit('removeProduct', state.basket.products.findIndex(item => dependent.id === item.id)));
                }
            }
        },
        relistProperty({ commit, state }, data) {
            return api.post('orders', data);
        }
    }
};
